import { Transition } from "@headlessui/react";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { clsx } from "../../modules/Utilkit/Utilkit";
import Flaticon from "../Flaticon/Flaticon";


const ContextureContext = createContext();

const useContexture = () => {
  return useContext(ContextureContext);
};

const ContextureProvider = ({ children }) => {
  const [ contexture, setContexture ] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    items: []
  });

  const hideContexture = () => setContexture((curr) => ({ ...curr, isVisible: false }));

  const value = {
    setContexture: ({ items, position }) => setContexture({ isVisible: true, position, items }),
    hideContexture
  };

  return (
    <ContextureContext.Provider value={ value }>
      { children }
      <ContexturePanel
        id="root"
        show={ contexture.isVisible }
        position={ contexture.position }
        onClose={ hideContexture }
      >
        <ContextureItem items={ contexture.items } position={ contexture.position } />
      </ContexturePanel>
    </ContextureContext.Provider>
  );
};

const ContextureItem = ({ items, position }) => {
  return items && items instanceof Array ? (
    items.map(({ id, name, icon, items, onClick, type, onSubmit }) => (
      items && items instanceof Array && items.length > 0 && type === 'form' ? (
        <Contexture key={ id } absolute={ true }>
          <ContextureButton>
            { icon && <Flaticon name={ icon.name } type={ icon.type } /> }
            <span>{ name }</span>
          </ContextureButton>

          <ContexturePanel position={ position } id={ id }>
            <form key={ id } onSubmit={ (e) => { e.preventDefault(); onSubmit && onSubmit(e); } }>
              { items.map(({ id, name, icon, type, options, selected, placeholder, value, onChange, className, props }) => (
                type === 'text' ? (
                  <input
                    { ...props }
                    key={ id }
                    type="text"
                    className={ clsx(className, "py-0.5 text-sm px-1 border border-gray-300 focus:ring-0 focus:border-blue-400 w-full rounded-md") }
                    placeholder={ placeholder }
                    value={ value }
                    onChange={ onChange }
                  />
                ) : type === 'number' ? (
                  <input
                    { ...props }
                    key={ id }
                    type="number"
                    className={ clsx(className, "py-0.5 text-sm px-1 border border-gray-300 focus:ring-0 focus:border-blue-400 w-full rounded-md") }
                    placeholder={ placeholder }
                    value={ value }
                    onChange={ onChange }
                  />
                ) : type === 'date' ? (
                  <input
                    { ...props }
                    key={ id }
                    type="date"
                    className={ clsx(className, "py-0.5 text-sm px-1 border border-gray-300 focus:ring-0 focus:border-blue-400 w-full rounded-md") }
                    placeholder={ placeholder }
                    value={ value }
                    onChange={ onChange }
                  />
                ) : type === 'button' ? (
                  <button
                    { ...props }
                    key={ id }
                    type="submit"
                    className="py-1 px-2 border border-gray-300 focus:ring-0 focus:border-blue-400 hover:bg-gray-100 text-start flex items-center space-x-2"
                  >
                    <Flaticon name={ icon.name } type={ icon.type } size={ icon.size } />
                    <span>{ name }</span>
                  </button>
                ) : type === 'multiselect' ? (
                  <ContextureMultiselect
                    key={ id }
                    options={ options }
                    selected={ selected }
                    onChange={ onChange }
                  />
                ) : null
              )) }
            </form>
          </ContexturePanel>
        </Contexture>
      ) : items && items instanceof Array && items.length > 0 ? (
        <Contexture key={ id } absolute={ true }>
          <ContextureButton>
            { icon && <Flaticon name={ icon.name } type={ icon.type } /> }
            <span>{ name }</span>
          </ContextureButton>

          <ContexturePanel position={ position } id={ id }>
            <ContextureItem items={ items } position={ position } />
          </ContexturePanel>
        </Contexture>
      ) : (
        <ContextureButton key={ id } onClick={ onClick }>
          { icon && <Flaticon name={ icon.name } type={ icon.type } /> }
          <span>{ name }</span>
        </ContextureButton>
      )
    ))
  ) : null;
};

const ContextureMultiselect = ({ options = [], selected = [], onChange = () => { } }) => {
  const [ selectedOptions, setSelectedOptions ] = useState(selected);

  const handleChange = (value) => {
    let newSelected;

    if (value === "all") {
      if (selectedOptions.length === options.length) {
        newSelected = [];
      } else {
        newSelected = options.map((option) => option.value);
      }
    } else {
      if (selectedOptions.includes(value)) {
        newSelected = selectedOptions.filter((s) => s !== value);
      } else {
        newSelected = [ ...selectedOptions, value ];
      }
    }
    setSelectedOptions(newSelected);
    onChange(newSelected);
  };

  return (
    <div className="flex flex-col gap-1 px-2 py-1">
      <button
        type="button"
        className="flex w-full justify-start rounded-md items-center px-3 py-0.5 hover:bg-gray-200 space-x-2 text-sm text-nowrap"
        onClick={ () => handleChange("all") }
        style={ { textAlign: "left" } }
      >
        { selectedOptions.length === options.length ? (
          <Flaticon name="checkbox" type="rr" />
        ) : (
          <Flaticon name="square" type="rr" />
        ) }
        <span className="ml-2">Select All</span>
      </button>

      { options.map(({ label, value }) => (
        <button
          key={ value }
          type="button"
          className="flex w-full justify-start rounded-md items-center px-3 py-0.5 hover:bg-gray-200 space-x-2 text-sm text-nowrap"
          onClick={ () => handleChange(value) }
          style={ { textAlign: "left" } }
        >
          { selectedOptions.includes(value) ? (
            <Flaticon name="checkbox" type="rr" />
          ) : (
            <Flaticon name="square" type="rr" />
          ) }
          <span className="ml-2">{ label }</span>
        </button>
      )) }
    </div>
  );
};


const ContextureButton = ({ children, onClick, className, disabled, loading }) => {
  return (
    <button
      type="button"
      disabled={ disabled || loading }
      className={ clsx(
        className ?? "flex w-full justify-start rounded-sm items-center px-3 py-0.5 hover:bg-gray-200 space-x-2 text-sm text-nowrap",
        (disabled || loading) && "opacity-50 cursor-not-allowed pointer-events-none"
      ) }
      onClick={ onClick }
    >
      { loading ? (
        <>
          <span className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin inline-block flex-shrink-0" />
          { children }
        </>
      ) : children }
    </button>
  );
};

const ContexturePanel = ({ id, show, children, onClose, position, absolute, className }) => {
  const isFirstOpen = useRef(true);

  useEffect(() => {
    if (!show) {
      isFirstOpen.current = true;
      return;
    }

    const handleClickOutside = (e) => {
      // Check if click is inside the current contexture panel
      if (e.target.closest('.contexture' + id) || e.target.classList.contains('contexture' + id)) return;
      
      // Check if click is inside any other contexture element (like Formify dropdowns)
      if (e.target.closest('.contexture') || e.target.classList.contains('contexture')) return;
      
      if (isFirstOpen.current) return;

      isFirstOpen.current = true;

      onClose();
    };

    window.addEventListener('mousedown', handleClickOutside);

    setTimeout(() => isFirstOpen.current = false, 50);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [ show, id, onClose ]);

  const defaultClassName = "border focus:outline-none border-gray-300 bg-white rounded-lg shadow-lg flex flex-col items-stretch justify-center p-1 min-w-36";
  const origin = absolute ? (
    position?.y > window.innerHeight * 0.75 && position?.x > window.innerWidth * 0.75 ? (
      "origin-bottom-right right-full bottom-0"
    ) : position?.y > window.innerHeight * 0.75 ? (
      "origin-bottom-left left-full bottom-0"
    ) : position?.x > window.innerWidth * 0.75 ? (
      "origin-top-right right-full top-0"
    ) : "origin-top-left left-full top-0"
  ) : position ? (
    position?.y > window.innerHeight * 0.75 && position?.x > window.innerWidth * 0.75 ? (
      "origin-bottom-right -translate-x-full -translate-y-full"
    ) : position?.y > window.innerHeight * 0.75 ? (
      "origin-bottom-left -translate-y-full"
    ) : position?.x > window.innerWidth * 0.75 ? (
      "origin-top-right -translate-x-full"
    ) : "origin-top-left"
  ) : null;

  return (
    <Transition
      as="div"
      show={ show }
      className={ clsx(
        absolute ? "absolute" : "fixed",
        className ?? defaultClassName,
        "z-50 text-nowrap outline-none contexture" + id,
        origin
      ) }
      style={ { left: !absolute && position?.x, top: !absolute && position?.y } }
      enter="transition transform duration-75 ease-out"
      enterFrom="opacity-0 scale-0"
      enterTo="opacity-100 scale-100"
      leave="transition transform duration-75 ease-in"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-0"
    >
      { children }
    </Transition>
  );
};

const Contexture = ({ children, absolute }) => {
  const [ show, setShow ] = useState(false);
  const buttonRef = useRef(null);
  const [ position, setPosition ] = useState({ x: 0, y: 0 });

  const handleClick = () => {
    if (show) {
      setShow(false);
    } else {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPosition({ x: rect.left, y: rect.bottom });
      }
      setShow(true);
    }
  };

  return (
    <div className="relative">
      { React.Children.map(children, (child, index) => {
        if (child.type === ContextureButton || child.type === ContextureSwitch) {
          return (
            <div ref={ buttonRef }>
              <ContextureButton
                key={ index }
                { ...child.props }
                onClick={ handleClick }
              >
                { child.props.children }
              </ContextureButton>
            </div>
          );
        } else if (child.type === ContexturePanel) {
          return (
            <ContexturePanel
              key={ index }
              { ...child.props }
              absolute={ absolute }
              position={ position }
              show={ show }
              onClose={ () => setShow(false) }
            >
              { child.props.children instanceof Function ? child.props.children({ onClose: () => setShow(false) }) : child.props.children }
            </ContexturePanel>
          );
        } else {
          return child;
        }
      }) }
    </div>
  );
};

// Legacy exports for backward compatibility
const ContextureSwitch = ContextureButton;
const ContextureCategory = ({ children, label }) => {
  return (
    <div className="border-t mt-2 pt-2.5 w-full flex flex-col items-stretch justify-center relative">
      { label && (
        <div className="text-sm max-w-full absolute bg-white top-0 left-[1px] pl-1 pr-2 -translate-y-1/2 text-gray-600 overflow-x-hidden overflow-ellipsis">
          { label }
        </div>
      ) }
      { children }
    </div>
  );
};

const ContextureRow = ({ children }) => {
  return (
    <div className="flex justify-start items-center px-1 text-nowrap rounded-sm py-0.5 w-full">
      { children }
    </div>
  );
};

const ContextureChild = ({ children }) => {
  const timeout = useRef(null);
  const [ show, setShow ] = useState(false);

  return (
    <>
      { children.map((child, index) => {
        if (child.type === ContextureButton) {
          return (
            <ContextureButton
              key={ index }
              className={ child.props.className }
              onMouseEnter={ () => {
                if (timeout.current) {
                  clearTimeout(timeout.current);
                };

                timeout.current = setTimeout(() => {
                  setShow(true);
                }, 500);
              } }
              onClick={ () => {
                if (timeout.current) {
                  clearTimeout(timeout.current);
                };

                setShow(true);
              } }
              onMouseLeave={ () => {
                if (timeout.current) {
                  clearTimeout(timeout.current);
                }
              } }
            >
              { child.props.children }
            </ContextureButton>
          );
        } else if (child.type === ContexturePanel) {
          return (
            <ContexturePanel
              absolute={ true }
              className={ child.props.className }
              key={ index }
              show={ show }
              onClose={ () => setShow(false) }
              position={ child.props.position }
              id={ child.props.id || 'child-' + index }
            >
              { child.props.children }
            </ContexturePanel>
          );
        } else {
          return null;
        }
      }) }
    </>
  );
};

export default ContextureProvider;
export {
    Contexture, ContextureButton, ContextureCategory, ContextureChild,
    ContextureItem, ContexturePanel, ContextureRow, ContextureSwitch, useContexture
};

